import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  NO_SEATS_MESSAGE,
  ROLE_ACCUSATIVE,
  ROLE_LABEL,
  availableSeatsLine,
  hasFreeSeat,
  invitePayload,
  newRequestId,
  payloadSignature,
  reservedSeatsLine,
  validateInviteDraft,
} from "../../lib/memberManagement";

const EMPTY = { first_name: "", last_name: "", email: "", phone: "", role: "", team_id: "" };

/**
 * Invite a Manager or a Sales user.
 *
 * WHEN THE ACTOR MAY INVITE EXACTLY ONE ROLE (Phase 2D.2 checkpoint 5) the role
 * selector is not rendered at all: the title names the role ("Добавяне на
 * търговец") and the role is stated. A Manager therefore cannot even express
 * "Мениджър" or "Собственик" - and the server refuses those regardless of what
 * this screen renders.
 *
 * WHAT THE DIALOG NEVER DOES
 *   * it never offers Собственик - the tenant channel cannot create an Owner, and
 *     the role list is built from what the DATABASE said the actor may invite;
 *   * it never computes a seat number: the live line and the disabled state both
 *     read the seat object the invitation list endpoint returned;
 *   * it never mints a new idempotency key for a retry of the SAME submission.
 *     The key changes only when the submitted values change, which is exactly
 *     what makes a replay a replay and a changed payload a conflict.
 *
 * Disabling the submit button when no seat is free is a courtesy. The backend and
 * the database enforce the limit regardless.
 */
export const InviteMemberDialog = ({
  open,
  roles,
  seats,
  submitting,
  serverError,
  fieldErrors,
  prefill,
  teams,
  canChooseTeam,
  managedTeam,
  onCancel,
  onSubmit,
}) => {
  const [draft, setDraft] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const requestIdRef = useRef("");
  const signatureRef = useRef("");
  const firstFieldRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setDraft({ ...EMPTY, ...(prefill || {}), role: prefill?.role || roles[0] || "" });
    setErrors({});
    requestIdRef.current = "";
    signatureRef.current = "";
    const timer = setTimeout(() => firstFieldRef.current?.focus(), 0);
    return () => clearTimeout(timer);
  }, [open, prefill, roles]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, submitting, onCancel]);

  // Phase 2D.3A - the OPTIONAL team of a Sales invitation. An Owner may choose
  // one of the ACTIVE teams; a Manager chooses nothing, because the server always
  // uses the team they manage - and a Manager without a team keeps the legacy
  // flow, which is what makes this rollout outage-free.
  const activeTeams = (teams || []).filter((t) => t.is_active);
  const teamPickerVisible = canChooseTeam === true && draft.role === "sales" && activeTeams.length > 0;
  const managedTeamVisible = !canChooseTeam && !!managedTeam && draft.role === "sales";

  const seat = seats?.[draft.role];
  const free = draft.role ? hasFreeSeat(seats, draft.role) : true;
  const reserved = useMemo(() => reservedSeatsLine(seat?.pending_invitations), [seat]);

  if (!open) return null;

  const set = (field) => (e) => {
    const value = e.target.value;
    setDraft((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const submit = (e) => {
    e.preventDefault();
    if (submitting) return;
    const found = validateInviteDraft(draft, roles);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    // ONE key per user action: the same values keep the key (a retry is a
    // replay), changed values earn a new one (a new decision).
    const signature = payloadSignature(draft);
    if (!requestIdRef.current || signatureRef.current !== signature) {
      requestIdRef.current = newRequestId();
      signatureRef.current = signature;
    }
    onSubmit({ ...invitePayload(draft), request_id: requestIdRef.current });
  };

  const fieldError = (name) => errors[name] || fieldErrors?.[name] || "";

  const field = (name, label, extra = {}) => {
    const message = fieldError(name);
    const id = `invite-${name}`;
    return (
      <div className="qw-field">
        <label htmlFor={id}>{label}</label>
        <input
          id={id}
          className="qw-input"
          value={draft[name]}
          onChange={set(name)}
          disabled={submitting}
          aria-invalid={message ? "true" : undefined}
          aria-describedby={message ? `${id}-error` : undefined}
          data-testid={id}
          ref={name === "first_name" ? firstFieldRef : undefined}
          {...extra}
        />
        {message && (
          <div className="qw-error" id={`${id}-error`} data-testid={`${id}-error`}>
            {message}
          </div>
        )}
      </div>
    );
  };

  const roleMessage = fieldError("role");
  // Phase 2D.2 checkpoint 5: the DATABASE decides what this actor may invite. An
  // Owner is normally offered Мениджър and Търговец; a Manager may only ever add
  // a Търговец, so there is nothing to choose and nothing to select.
  const fixedRole = roles.length === 1 ? roles[0] : null;

  return (
    <div className="qw-modal-backdrop">
      <form
        className="qw-modal qw-modal-wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="invite-dialog-title"
        onSubmit={submit}
        data-testid="invite-member-dialog"
      >
        <div className="h" id="invite-dialog-title">
          {fixedRole ? `Добавяне на ${ROLE_ACCUSATIVE[fixedRole]}` : "Добави потребител"}
        </div>
        <div className="body">
          <p className="qw-help" style={{ marginTop: 0 }}>
            Поканеният ще получи имейл с лична връзка. Мястото се запазва веднага
            след създаването на поканата.
          </p>

          <div className="qw-field">
            <label htmlFor={fixedRole ? undefined : "invite-role"}>Роля</label>
            {fixedRole ? (
              // Phase 2D.2 checkpoint 5 - ONE invitable role means NO choice to
              // make. A Manager may only ever add a Търговец, so the role is
              // stated instead of being offered as a select with one option.
              <div className="qw-help" data-testid="invite-role-fixed">
                <strong>{ROLE_LABEL[fixedRole]}</strong>
              </div>
            ) : (
              <select
                id="invite-role"
                className="qw-select"
                value={draft.role}
                onChange={set("role")}
                disabled={submitting}
                aria-invalid={roleMessage ? "true" : undefined}
                aria-describedby={roleMessage ? "invite-role-error" : "invite-seat-line"}
                data-testid="invite-role"
              >
                {roles.length === 0 && <option value="">Няма достъпни роли</option>}
                {roles.map((role) => (
                  <option key={role} value={role} data-testid={`invite-role-option-${role}`}>
                    {ROLE_LABEL[role]}
                  </option>
                ))}
              </select>
            )}
            {roleMessage && (
              <div className="qw-error" id="invite-role-error" data-testid="invite-role-error">
                {roleMessage}
              </div>
            )}
            {seat && (
              <div className="qw-help" id="invite-seat-line" data-testid="invite-seat-line">
                {availableSeatsLine(draft.role, seat)}
              </div>
            )}
            {reserved && (
              <div className="qw-help" data-testid="invite-seat-reserved">
                {reserved}
              </div>
            )}
            {draft.role && !free && (
              <div className="qw-error" data-testid="invite-no-seats">
                {NO_SEATS_MESSAGE}
              </div>
            )}
          </div>

          <div className="qw-grid cols-2">
            {field("first_name", "Име", { autoComplete: "given-name", maxLength: 100 })}
            {field("last_name", "Фамилия", { autoComplete: "family-name", maxLength: 100 })}
          </div>
          {field("email", "Имейл", { type: "email", autoComplete: "email", maxLength: 320 })}
          {field("phone", "Телефон (по желание)", { autoComplete: "tel", maxLength: 40 })}

          {teamPickerVisible && (
            <div className="qw-field">
              <label htmlFor="invite-team">Екип (по желание)</label>
              <select
                id="invite-team"
                className="qw-select"
                value={draft.team_id}
                onChange={set("team_id")}
                disabled={submitting}
                data-testid="invite-team"
              >
                <option value="">Без екип</option>
                {activeTeams.map((team) => (
                  <option key={team.id} value={team.id} data-testid={`invite-team-option-${team.id}`}>
                    {team.name}
                  </option>
                ))}
              </select>
              <div className="qw-help" data-testid="invite-team-help">
                Изборът на екип не използва допълнително лицензирано място.
              </div>
            </div>
          )}

          {managedTeamVisible && (
            <div className="qw-field">
              <label htmlFor={undefined}>Екип</label>
              <div className="qw-help" data-testid="invite-team-fixed">
                <strong>{managedTeam.name}</strong>
              </div>
            </div>
          )}

          {serverError && (
            <div className="qw-error" role="alert" data-testid="invite-dialog-error">
              {serverError}
            </div>
          )}
        </div>
        <div className="footer">
          <button
            type="button"
            className="qw-btn ghost"
            onClick={onCancel}
            disabled={submitting}
            data-testid="invite-dialog-cancel"
          >
            Отказ
          </button>
          <button
            type="submit"
            className="qw-btn primary"
            disabled={submitting || !draft.role || !free}
            data-testid="invite-dialog-submit"
          >
            {submitting ? "Изпращане…" : "Изпрати поканата"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default InviteMemberDialog;
